import * as React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import PeopleIcon from "@mui/icons-material/People";
import AssignmentIcon from "@mui/icons-material/Assignment";
import {
    HistoryEdu,
    Queue,
    Reorder,
    VerifiedUser,
} from "@mui/icons-material";
import { SelectedMenu } from "@/redux/modules/Header";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ListIcon from '@mui/icons-material/List';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import { useSelector } from 'react-redux';
import { selectDepartmentList } from '@/redux/modules/department/departmentSelectors';
import { Badge, keyframes } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { RolesData } from "@/interface";
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import { AcceptsType, QueueEnum, RequestStatusEnum } from '@/constatns';
import { StateReducer } from '@/redux/modules/reducer';
import { isProdHost } from '@/core/config';
import {
    IHelpdeskCreateRequest as HelpdeskModel,
} from "@/redux/modules/Helpdesk";
import helpdeskNotificationSound from '@/sounds/helpdesk.mp3';
import BarChartIcon from '@mui/icons-material/BarChart';
import { searchRequest } from "@/api/MyApi";
import { api } from "@/api/Api";
import { QueueResponse } from "@/api/CamundaController";
import { filterAcceptDepartments, hasAcceptDepartmentScope, hasAnyRole } from "@/core/helpers/PageHelpers";
import SearchIcon from '@mui/icons-material/Search';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InsightsIcon from '@mui/icons-material/Insights';
import EventNoteIcon from '@mui/icons-material/EventNote';

const pulseBackground = keyframes`
    0% {
        background-color: white;
    }
    50% {
        background-color: #ffd7d7;
    }
    100% {
        background-color: white;
    }
`;

const menuList = ({ mainCashierHaveOpenKassa, workWithKassa = isProdHost ? false : true, cashboxPaymentNearCount = 0, haveActiveNeedAccept = false, hdListCount = 0, haveReturnAccepted = false, haveActiveRequest = false }: { mainCashierHaveOpenKassa?: boolean, workWithKassa?: boolean, cashboxPaymentNearCount?: number, haveActiveNeedAccept?: boolean, hdListCount?: number, haveReturnAccepted?: boolean, haveActiveRequest?: boolean, }) => [
    { title: "Курсы валют", path: "/treasurer-rates", icon: <MonetizationOnIcon /> },
    { title: "Документы", path: "/documents", icon: <PictureAsPdfIcon /> },
    { title: "Управленческая отчетность", path: "/management-reports", icon: <BarChartIcon /> },
    ...((workWithKassa || mainCashierHaveOpenKassa) ? [{ title: "Операционные процессы", path: "/cash-balance", icon: <CurrencyExchangeIcon /> }] : []),
    { title: "Бухгалтерия", path: "/accountant", icon: <CurrencyExchangeIcon /> },
    { title: "История операций", path: "/request-list", icon: <Badge badgeContent={haveReturnAccepted || haveActiveRequest ? "!" : null} color="secondary"><ListIcon /></Badge>, },
    { title: "Сверка", path: "/reconciliation", icon: <SearchIcon />, },
    { title: "Ревизия", path: "/audit", icon: <FactCheckIcon /> },
    { title: "Аналитика", path: "/analytics", icon: <InsightsIcon /> },
    { title: "Встречи", path: "/meetings", icon: <EventNoteIcon /> },
    { title: "Табло", path: "/tablo", icon: <DashboardIcon /> },
    { title: "Касса ОФД", path: "/payment-control", icon: <Badge badgeContent={cashboxPaymentNearCount} color="secondary"><PointOfSaleIcon /></Badge> },
    {
        title: "Акцепты", path: "/accepts", icon: <Badge badgeContent={haveActiveNeedAccept ? "!" : null} anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }} color="error"><GppMaybeIcon /></Badge>
    },
    { title: "Helpdesk", path: "/helpdesk", icon: <Badge badgeContent={hdListCount} color="secondary"> <SupportAgentIcon /></Badge> },
    { title: "Открытие отделений и касс", path: "/department-open", icon: <AddBusinessIcon /> },
    { title: "Сотрудники", path: "/employees", icon: <PeopleIcon /> },
    { title: "Очереди", path: "/queue", icon: <Queue /> },
    { title: "История", path: "/history", icon: <HistoryEdu /> },
    { title: "Пользователи", path: "/users", icon: <PeopleIcon /> },
    { title: "Роли", path: "/roles", icon: <VerifiedUser /> },
    { title: "Настройки очередей", path: "/queues", icon: <Reorder /> },
];

interface MainListItemsProps {
    selectedMenu?: SelectedMenu;
    onSelectMenu: (menu: SelectedMenu) => void;
    rolesData?: RolesData;
}

export const MainListItems = (props: MainListItemsProps) => {
    const { onSelectMenu, rolesData } = props;

    const departmentList = useSelector(selectDepartmentList);
    const helpdeskList = useSelector((state: StateReducer) => state.helpdesk.hdList);
    const helpdeskAssigneeList = useSelector((state: StateReducer) => state.helpdesk.hdAssigneeList);
    const [haveActiveRequest, setHaveActiveRequest] = React.useState(false);
    const acceptsQueue = useSelector((state: StateReducer) => state?.header?.acceptsQueue);
    const cashierRequestQueue = useSelector((state: StateReducer) => state?.header?.cashierRequestQueue);
    const amountTickets = (helpdeskList && helpdeskList?.length || 0) + (helpdeskAssigneeList && helpdeskAssigneeList?.length || 0);
    const workWithKassa = useSelector((state: StateReducer) => state?.header?.workWithKassa);
    const userLogin = useSelector((state: StateReducer) => state?.header?.userContext?.user?.login);
    const kassaQueueList = useSelector((state: StateReducer) => state?.header?.userQueue?.find((queue) => queue?.process === QueueEnum.KassaDay));
    const kassaId = useSelector((state: StateReducer) => state?.departmentReducer?.activeKassaId);
    const departmentId = useSelector((state: StateReducer) => state?.departmentReducer?.activeDepartmentId);

    const prevTicketsByRoleRef = React.useRef<HelpdeskModel[]>([]);
    const prevAssigneeTicketsRef = React.useRef<HelpdeskModel[]>([]);
    const [canPlay, setCanPlay] = React.useState(false);

    const haveActiveNeedAccept = React?.useMemo(() => {
        let boolean = false;
        const hasDepScope = hasAcceptDepartmentScope(rolesData);
        // Возвраты и валюта - с тенантами из AcceptTenant_<id>, брони и индивы - без них.
        const directorAcceptDepartments = filterAcceptDepartments(departmentList, rolesData, { withAcceptTenants: true });
        const acceptDepartments = filterAcceptDepartments(departmentList, rolesData);

        if (acceptsQueue && acceptsQueue?.length > 0) {
            boolean = acceptsQueue?.filter(a => hasDepScope ? directorAcceptDepartments?.some(dep => dep?.id === +a?.depId) : true)
                ?.some(
                    (r) =>
                        (r.acceptType === AcceptsType.ReturnRequest && r.statusReturn === RequestStatusEnum.Waiting)
                        || (r.acceptType === AcceptsType.AddCurrency)
                        || (r.acceptType === AcceptsType.DeleteCurrency)
                );
        }

        if (!boolean && cashierRequestQueue && cashierRequestQueue?.length > 0 && (hasAnyRole(rolesData, ["isTreasurer", "isDirector", "isAdmin"]))) {
            boolean = cashierRequestQueue?.filter(a => hasDepScope ? acceptDepartments?.some(dep => dep?.id === +a?.depsId) : true)
                ?.some(
                    (r) =>
                        (+r.requestStatus === 8 && r.acceptStatus === RequestStatusEnum.Waiting)
                        || (r.isIndividual === "true" && (hasAnyRole(rolesData, ["isDirector", "isAdmin"]) ? r.acceptStatus === RequestStatusEnum.Waiting || r.acceptStatus === RequestStatusEnum.AcceptedByTreasurer : r.acceptStatus === RequestStatusEnum.Waiting))
                );
        }

        return boolean;
    }, [acceptsQueue, cashierRequestQueue, departmentList, rolesData]);

    const haveReturnAccepted = React.useMemo(() => acceptsQueue?.some((r) => r?.acceptType === AcceptsType?.ReturnRequest && r?.statusReturn === RequestStatusEnum?.Accepted && r?.initiator === userLogin), [acceptsQueue]);

    const [mainCashierHaveOpenKassa, setMainCashierHaveOpenKassa] = React.useState(false);

    React.useEffect(() => {
        const enableAudio = () => setCanPlay(true);
        window.addEventListener("click", enableAudio, { once: true });

        if (rolesData?.mainCashierCity && !workWithKassa) {
            kassaQueueList && api.camunda.getQueueList(kassaQueueList?.id, 1, 100)
                .then(async (res: QueueResponse) => {
                    const currentProcess = res?.queue?.find((queueRes) => queueRes.login.toLowerCase() === userLogin?.toLowerCase());
                    if (currentProcess) {
                        setMainCashierHaveOpenKassa(true);

                        return;
                    }
                });
        }

        return () => window.removeEventListener("click", enableAudio);
    }, []);

    // Вызывать только в ветках меню, где есть /payment-control.
    const countCashboxPaymentNear = () => {
        let paymentNearCount = 0;

        departmentList?.forEach((dep) => {
            dep?.kassa?.forEach((k) => {
                if (isLessThanMonthLeft(k?.payment, k?.paymentPeriod)) {
                    paymentNearCount += 1;
                }
            });
        });

        return paymentNearCount;
    };

    React.useEffect(() => {
        if (rolesData?.isCashier) {
            checkRequests();
            const id = setInterval(() => {
                checkRequests();
            }, 10000);

            return () => {
                clearInterval(id);
            };
        }
    }, []);

    const checkRequests = () => {
        const startDate = (new Date()).setHours(5, 0, 0);
        const endDate = (new Date()).setHours(28, 59, 59);
        searchRequest({
            businessKey: null,
            status: 9,
            iin: null,
            amount: null,
            currencyCode: null,
            actionType: null,
            lastname: null,
            firstname: null,
            middlename: null,
            phone: null,
            startDate: new Date(startDate),
            endDate: new Date(endDate),
            depId: [+departmentId],
            initiator: null,
            afmStatus: null, // 0 - Не нужно отправлять, 1 - Нужно отправить, 2 - Сохранено, 3 - Подписано ЭЦП
            fromSum: null,
            toSum: null,
            kassaId
        })
            .then(async (res) => {
                setHaveActiveRequest(res?.model?.length > 0);
            })
            .catch(e => {
                console.error(e);
            });
    };

    function isLessThanMonthLeft(payment: string, paymentPeriod: number) {
        // Дата оплаты
        const paymentDate = new Date(payment);
        // Дата окончания периода оплаты
        const nextPaymentDate = new Date(paymentDate);
        nextPaymentDate.setMonth(nextPaymentDate.getMonth() + paymentPeriod);

        // Сегодня
        const now = new Date();
        // Разница в миллисекундах
        const diffMs = +nextPaymentDate - +now;
        // Разница в днях
        const diffDays = diffMs / (1000 * 60 * 60 * 24);

        // true, если осталось меньше 30 дней
        return diffDays < 30;
    }

    const filtredMenuList = React.useMemo(() => {
        // сравниваем с прошлым состоянием
        if ((helpdeskList.length > prevTicketsByRoleRef.current.length || helpdeskAssigneeList.length > prevAssigneeTicketsRef.current.length) && canPlay) {
            const audio = new Audio(helpdeskNotificationSound);
            audio.play().catch(err => console.warn("play() blocked", err));
        }

        prevTicketsByRoleRef.current = helpdeskList;
        prevAssigneeTicketsRef.current = helpdeskAssigneeList;

        if (rolesData?.isAdmin) {
            return menuList({ cashboxPaymentNearCount: countCashboxPaymentNear(), haveActiveNeedAccept, hdListCount: amountTickets, haveReturnAccepted })
                .filter((item) => isProdHost ? item.path !== "/cash-balance" : true);
        } else if (rolesData?.isDirector && !rolesData?.franchiseTenantId) {
            return menuList({ haveActiveNeedAccept, hdListCount: amountTickets }).filter((item) => item.path.match(/^(\/treasurer-rates|\/documents|\/request-list|\/helpdesk|\/accepts|\/reconciliation|\/audit|\/employees)$/gi));
        } else if (rolesData?.isDirector && rolesData?.franchiseTenantId) {
            return menuList({ haveActiveNeedAccept, hdListCount: amountTickets }).filter((item) => item.path.match(/^(\/treasurer-rates|\/documents|\/request-list|\/helpdesk|\/accepts|\/reconciliation|\/employees)$/gi));
        } else if (rolesData?.isTreasurer) {
            return menuList({ haveActiveNeedAccept, hdListCount: amountTickets }).filter((item) => item.path.match(/^(\/treasurer-rates|\/documents|\/request-list|\/accepts|\/helpdesk)$/gi));
        } else if (rolesData?.isTreasurerViewer) {
            return menuList({}).filter((item) => item.path.match(/^(\/treasurer-rates|\/request-list|\/accepts)$/gi));
        } else if (rolesData?.isAccountant) {
            return menuList({ cashboxPaymentNearCount: countCashboxPaymentNear(), hdListCount: amountTickets }).filter((item) => item.path.match(/^(\/treasurer-rates|\/documents|\/request-list|\/payment-control|\/accountant|\/helpdesk|\/audit|\/employees)$/gi));
        } else if (rolesData?.isAnalyst) {
            return menuList({ hdListCount: amountTickets }).filter((item) => item.path.match(/^(\/treasurer-rates|\/documents|\/management-reports|\/request-list|\/helpdesk)$/gi));
        } else if (rolesData?.isCashier) {
            return menuList({ workWithKassa: true, hdListCount: amountTickets, haveReturnAccepted, haveActiveRequest }).filter((item) => item.path.match(/^(\/documents|\/request-list|\/cash-balance|\/tablo|\/helpdesk)$/gi));
        } else if (rolesData?.mainCashierCity) {
            return menuList({ mainCashierHaveOpenKassa, workWithKassa, haveActiveNeedAccept, hdListCount: amountTickets, haveReturnAccepted }).filter((item) => item.path.match(/^(\/documents|\/request-list|\/cash-balance|\/tablo|\/helpdesk|\/accepts|\/reconciliation|\/audit|\/employees)$/gi));
        } else if (rolesData?.managerCity) {
            return menuList({}).filter((item) => item.path.match(/^(\/documents|\/treasurer-rates|\/request-list|\/helpdesk|\/reconciliation|\/audit)$/gi));
        } else if (rolesData?.isHR) {
            return menuList({ hdListCount: amountTickets, }).filter((item) => item.path.match(/^(\/documents|\/helpdesk)$/gi));
        } else if (rolesData?.isTablo) {
            return menuList({ hdListCount: amountTickets }).filter((item) => item.path.match(/^(\/helpdesk|\/tablo)$/gi));
        } else {
            return [];
        }
    }, [rolesData, haveActiveNeedAccept, helpdeskList, helpdeskAssigneeList, mainCashierHaveOpenKassa, haveActiveRequest, departmentList]);

    return (
        <React.Fragment>
            {filtredMenuList.map((item, i) => (
                <ListItemButton
                    sx={{
                        height: "56px",
                        animation: item.path === "/accepts" && haveActiveNeedAccept ? `${pulseBackground} 1s infinite` : "none"
                    }}
                    key={item.title}
                    onClick={() => {
                        if (item.path === "/tablo") {
                            window.open(`${window.location.origin}/tablo`, '_blank');
                        } else {
                            onSelectMenu({ path: item.path, title: item.title, index: i });
                        }
                    }}
                >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText secondary={item.title} />
                </ListItemButton>
            ))
            }
        </React.Fragment >
    );
};

export const SecondaryListItems = (
    <React.Fragment>
        <ListSubheader component="div" inset>
            Saved reports
        </ListSubheader>
        <ListItemButton>
            <ListItemIcon>
                <AssignmentIcon />
            </ListItemIcon>
            <ListItemText secondary="Current month" />
        </ListItemButton>
        <ListItemButton>
            <ListItemIcon>
                <AssignmentIcon />
            </ListItemIcon>
            <ListItemText secondary="Last quarter" />
        </ListItemButton>
        <ListItemButton>
            <ListItemIcon>
                <AssignmentIcon />
            </ListItemIcon>
            <ListItemText secondary="Year-end sale" />
        </ListItemButton>
    </React.Fragment>
);
